import { Box, Button, Container, Drawer, IconButton, ListItemIcon, Menu, MenuItem, Stack, TextField, InputAdornment } from "@mui/material";
import { NavLink } from "react-router-dom";
import Basket from "../headers/Basket";
import React, { useEffect, useRef } from "react";
import { CartItem } from "../../../lib/types/search";
import { useGlobals } from "../../hooks/useGlobals";
import { serverApi } from "../../../lib/config";
import { Logout } from "@mui/icons-material";
import MenuIcon from "@mui/icons-material/Menu";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { useHistory } from "react-router-dom";
import { applyMagneticNavEffect } from "./applyMagneticNavEffect";

interface HomeNavbarProps {
  cartItems: CartItem[];
  onAdd: (item: CartItem) => void;
  onRemove: (item: CartItem) => void;
  onDelete: (item: CartItem) => void;
  onDeleteAll: () => void;
  setSignupOpen: (isOpen: boolean) => void;
  setLoginOpen: (isOpen: boolean) => void;
  handleLogoutClick: (e: React.MouseEvent<HTMLElement>) => void;
  anchorEl: HTMLElement | null;
  handleCloseLogout: () => void;
  handleLogoutRequest: () => void;
}

export default function HomeNavBar(props: HomeNavbarProps) {
  const {
    cartItems,
    onAdd,
    onRemove,
    onDelete,
    onDeleteAll,
    setSignupOpen,
    setLoginOpen,
    handleLogoutClick,
    anchorEl,
    handleCloseLogout,
    handleLogoutRequest,
  } = props;

  const { authMember } = useGlobals();
  const history = useHistory();
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");

  const goToProducts = () => {
    history.push("/products");
    setMobileOpen(false);
  };

  const navRootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const root = navRootRef.current;
    if (!root) return;

    const links = Array.from(root.querySelectorAll<HTMLElement>(".nav-link"));
    if (links.length === 0) return;

    return applyMagneticNavEffect(root, links, {
      distance: 30,
      strength: 0.28,
      maxOffset: 12,
      spring: 0.12,
    });
  }, []);

  return (
    <div className="home-navbar" ref={navRootRef}>
      <Box className="nav-shell">
        <Box className="nav-inner">
          <Box className="nav-left">
            <IconButton
              className="nav-burger"
              aria-label="Open menu"
              onClick={() => setMobileOpen(true)}
              size="small"
            >
              <MenuIcon />
            </IconButton>
            <NavLink to="/" className="nav-brand" onClick={() => setMobileOpen(false)}>
              <span className="nav-brand-mark">
                <img className="nav-brand-mark-img" src="/icons/qamar-logo.png" alt="Qamar Books logo" />
              </span>
              <span className="nav-brand-text">
                <span className="nav-brand-name">
                  <span className="nav-brand-name__primary">Qamar</span>
                  <span className="nav-brand-name__secondary">Books</span>
                </span>
              </span>
            </NavLink>
          </Box>

          <Box className="nav-center">
            <NavLink exact to="/" className="nav-link" activeClassName="nav-link--active">
              Home
            </NavLink>
            <NavLink to="/products" className="nav-link" activeClassName="nav-link--active">
              Books
            </NavLink>
            {authMember && (
              <NavLink to="/orders" className="nav-link" activeClassName="nav-link--active">
                Orders
              </NavLink>
            )}
            {authMember && (
              <NavLink
                to="/member-page"
                className="nav-link"
                activeClassName="nav-link--active"
              >
                My Page
              </NavLink>
            )}
            <NavLink to="/about" className="nav-link" activeClassName="nav-link--active">
              About Us
            </NavLink>
            <NavLink to="/help" className="nav-link" activeClassName="nav-link--active">
              Help
            </NavLink>
          </Box>

          <Box className="nav-right">
            <TextField
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") goToProducts();
              }}
              placeholder="Search books…"
              size="small"
              className="nav-search"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />

            <Basket
              cartItems={cartItems}
              onAdd={onAdd}
              onRemove={onRemove}
              onDelete={onDelete}
              onDeleteAll={onDeleteAll}
            />

            {!authMember ? (
              <Box className="nav-auth">
                <Button
                  variant="outlined"
                  className="nav-btn nav-btn--ghost"
                  onClick={() => setLoginOpen(true)}
                >
                  Login
                </Button>
              </Box>
            ) : (
              <button
                className="nav-avatarBtn"
                onClick={handleLogoutClick}
                aria-haspopup="true"
              >
                <img
                  className="nav-avatar"
                  alt="User avatar"
                  src={
                    authMember?.memberImage
                      ? `${serverApi}/${authMember?.memberImage}`
                      : "/icons/default-user.svg"
                  }
                />
              </button>
            )}

            <Menu
              anchorEl={anchorEl}
              id="account-menu"
              open={Boolean(anchorEl)}
              onClose={handleCloseLogout}
              onClick={handleCloseLogout}
              PaperProps={{
                elevation: 0,
                sx: {
                  overflow: "visible",
                  filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
                  mt: 1.5,
                  "&:before": {
                    content: '""',
                    display: "block",
                    position: "absolute",
                    top: 0,
                    right: 14,
                    width: 10,
                    height: 10,
                    bgcolor: "background.paper",
                    transform: "translateY(-50%) rotate(45deg)",
                    zIndex: 0,
                  },
                },
              }}
              transformOrigin={{ horizontal: "right", vertical: "top" }}
              anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            >
              <MenuItem onClick={handleLogoutRequest}>
                <ListItemIcon>
                  <Logout fontSize="small" style={{ color: "blue" }} />
                </ListItemIcon>
                Logout
              </MenuItem>
            </Menu>
          </Box>
        </Box>
      </Box>

      <Drawer
        anchor="left"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        PaperProps={{ className: "nav-drawer" }}
      >
        <Box className="nav-drawerHead">
          <Box className="nav-drawerBrand">
            <img className="nav-drawer-brand-img" src="/icons/qamar-logo.png" alt="Qamar Books logo" />
            <span className="nav-brand-name">
              <span className="nav-brand-name__primary">Qamar</span>
              <span className="nav-brand-name__secondary">Books</span>
            </span>
          </Box>
          <IconButton aria-label="Close menu" onClick={() => setMobileOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Box className="nav-drawerBody">
          <button className="nav-drawerLink" onClick={() => history.push("/" )}>Home</button>
          <button className="nav-drawerLink" onClick={goToProducts}>Products</button>
          {authMember && (
            <button
              className="nav-drawerLink"
              onClick={() => {
                history.push("/orders");
                setMobileOpen(false);
              }}
            >
              Orders
            </button>
          )}
          {authMember && (
            <button
              className="nav-drawerLink"
              onClick={() => {
                history.push("/member-page");
                setMobileOpen(false);
              }}
            >
              My Page
            </button>
          )}
          <button
            className="nav-drawerLink"
            onClick={() => {
              history.push("/about");
              setMobileOpen(false);
            }}
          >
            About
          </button>
          <button
            className="nav-drawerLink"
            onClick={() => {
              history.push("/help");
              setMobileOpen(false);
            }}
          >
            Help
          </button>
          {!authMember && (
            <Box className="nav-drawerAuth">
              <Button
                variant="outlined"
                fullWidth
                onClick={() => {
                  setLoginOpen(true);
                  setMobileOpen(false);
                }}
              >
                Login
              </Button>
            </Box>
          )}
        </Box>
      </Drawer>

      <Container className="navbar-container">
        <Stack className={"header-frame"}>
          <Stack
            className={`detail home-hero-copy home-hero-detail ${
              authMember ? "home-hero-detail--member" : "home-hero-detail--guest"
            }`}
          >
            {/* Hero copy — guest vs member visuals (nav menu unchanged) */}
            {!authMember ? (
              <>
                <Box className={"home-hero-welcome"}>WELCOME TO QAMAR BOOKS</Box>

                <Box className={"head-main-txt"} component="h1">
                  <span className="head-main-line head-main-line--lead">
                    Your next great read
                  </span>
                  <span className="head-main-line head-main-line--accent">
                    start with one click
                  </span>
                </Box>

                <Box className={"home-hero-features"}>
                  <Box className={"home-hero-feature"}>Curated collections</Box>
                  <Box className={"home-hero-feature"}>Fast discovery</Box>
                  <Box className={"home-hero-feature"}>Secure checkout</Box>
                </Box>
              </>
            ) : (
              <>
                <Box className={"home-hero-welcome"}>
                  WELCOME BACK
                  {authMember.memberNick
                    ? ` · ${authMember.memberNick}`
                    : ""}
                </Box>

                <Box className={"head-main-txt"} component="h1">
                  <span className="head-main-line head-main-line--lead">
                    Your reading journey
                  </span>
                  <span className="head-main-line head-main-line--accent">
                    continues right here
                  </span>
                </Box>

                <Box className={"home-hero-features"}>
                  <Box className={"home-hero-feature"}>Orders & history</Box>
                  <Box className={"home-hero-feature"}>Saved & liked</Box>
                  <Box className={"home-hero-feature"}>Member perks</Box>
                </Box>
              </>
            )}

            <Box
              className={`home-hero-actions${
                authMember ? " home-hero-actions--member" : ""
              }`}
            >
              {!authMember && (
                <Button
                  variant={"outlined"}
                  className={"home-hero-cta-secondary"}
                  onClick={() => setSignupOpen(true)}
                >
                  Sign up
                </Button>
              )}

              <Button
                variant={"contained"}
                className={"home-hero-cta-primary"}
                onClick={() => window.location.assign("/products")}
              >
                Browse Books
              </Button>
            </Box>
          </Stack>
        </Stack>
      </Container>
    </div>
  );
}
